import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import defaultUserPhoto from '../../../images/default_user.png';
import spinner from '../../../images/spinner.gif';
import AddressInputAutoComplete from '../../common/AddressInputAutoComplete';
import ChangeUserPasswordModal from '../../ChangeUserPasswordModal';
import Edit from '../../common/forms/buttons/Edit';
import Cancel from '../../common/forms/buttons/Cancel';
import ErrorMessage from '../../common/forms/ErrorMessage';
import { genderList } from '../../../constants/general';
import SwitchRow from '../../common/forms/SwitchRow';
import Radio from '../../common/forms/Radio';
import Loader from '../../common/ui/Loader';
import Rating from '../../common/ui/Rating';

export default class WorkerPersonalInfo extends Component {
  static propTypes = {
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
    photo_image_url: PropTypes.string.isRequired,
    gender: PropTypes.string.isRequired,
    language_id: PropTypes.number.isRequired,
    secondary_language_id: PropTypes.number,
    has_car: PropTypes.bool.isRequired,
    address: PropTypes.string.isRequired,
    address2: PropTypes.string,
    state: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
    isEditing: PropTypes.bool.isRequired,
    enableEditingPersonalInfo: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleChangeAddress: PropTypes.func.isRequired,
    handleChangePrimaryLanguage: PropTypes.func.isRequired,
    handleChangePassword: PropTypes.func.isRequired,
    handleSwitch: PropTypes.func.isRequired,
    cancelEditingPersonalInfo: PropTypes.func,
    savePersonalInfo: PropTypes.func.isRequired,
    validationErrors: PropTypes.object.isRequired,
    uploadFile: PropTypes.func,
    isLoadingPersonalInfo: PropTypes.bool,
    setNewUserPhoto: PropTypes.func,
    userNewPhotoUrl: PropTypes.string,
    languagesList: PropTypes.array
  };

  state = {
    uploadingUserPhoto: false
  };

  handleUploadUserPhoto = event => {
    this.setState({
      uploadingUserPhoto: true
    });
    const form = event.target.parentElement;
    const { id, uploadFile, setNewUserPhoto } = this.props;
    uploadFile(form, id, false)
      .then(data => {
        this.setState({
          uploadingUserPhoto: false
        });
        setNewUserPhoto(data.data.file_url);
      })
      .catch(err => {
        this.setState({ uploadingUserPhoto: false });
        console.error(err);
      });
  };

  render () {
    const { isEditing, handleChange, handleChangeAddress, validationErrors, isLoadingPersonalInfo, userNewPhotoUrl,
      languagesList, handleChangePrimaryLanguage, cancelEditingPersonalInfo, savePersonalInfo, enableEditingPersonalInfo,
      handleChangePassword, handleSwitch, first_name, last_name, email, phone, photo_image_url, gender, language_id,
      secondary_language_id, has_car, address, address2, state, city, zip, worker } = this.props;
    const { uploadingUserPhoto } = this.state;
    const addressInfo = { address, state, city, zip };
    const address2IsShown = isEditing || address2;

    const availableSecondaryLang = languagesList.filter(lang => lang.id !== language_id); // exclude primary language
    const userLang = languagesList.find(lang => lang.id === language_id);
    const secondUserLang = languagesList.find(lang => lang.id === secondary_language_id);

    return (
      <section className='personal-info'>
        <div className='section-header-wrapper'>
          <h4>Personal Information</h4>

          { !isLoadingPersonalInfo && (
            <div className='ml-3'>
              {isEditing && (
                <button className='button button-primary button-icon' onClick={savePersonalInfo}>
                  <FontAwesomeIcon icon={faSave} />
                </button>
              )}
              {isEditing ? (
                <Cancel onClick={cancelEditingPersonalInfo} />
              ) : (
                <Edit onClick={enableEditingPersonalInfo} />
              )}
            </div>
          )}
        </div>

        { isLoadingPersonalInfo ? <div className='loader-wrapper'><Loader /></div> : (
          <Fragment>
            <div className='section-row align-center'>
              <div
                className='image-icon'
                style={{ backgroundImage: `url('${userNewPhotoUrl || photo_image_url || defaultUserPhoto}')` }}
              />
              { isEditing && (
                <React.Fragment>
                  { uploadingUserPhoto ? (
                    <img className='loading-spinner' src={spinner} alt='spinner' />
                  ) : (
                    <form
                      className='upload-user-photo'
                      encType='multipart/form-data'
                    >
                      <label htmlFor='user-photo' className='change-photo-link'>Change Photo</label>
                      <input
                        hidden
                        id='user-photo'
                        type='file'
                        name='file'
                        onChange={this.handleUploadUserPhoto}
                      />
                    </form>
                  )}
                </React.Fragment>
              )}
            </div>

            {worker.average_rating && (
              <div className='mt-1'>
                <Rating value={worker.average_rating} disabled buttonClassName='btn-rate-sm' />
              </div>
            )}

            <div className='section-row flex-wrap'>
              <span className='section-row-label'>First Name:</span>
              { isEditing ? (
                <div className='input-wrapper w-100'>
                  <input
                    className='input'
                    name='first_name'
                    value={first_name || ''}
                    placeholder='First Name'
                    onChange={handleChange}
                  />
                  <ErrorMessage errorText={validationErrors.first_name} />
                </div>
              ) : (
                <p>{first_name || ''}</p>
              )}
            </div>

            <div className='section-row flex-wrap'>
              <span className='section-row-label'>Last Name:</span>
              { isEditing ? (
                <div className='input-wrapper w-100'>
                  <input
                    className='input'
                    name='last_name'
                    value={last_name || ''}
                    placeholder='Last Name'
                    onChange={handleChange}
                  />
                  <ErrorMessage errorText={validationErrors.last_name} />
                </div>
              ) : (
                <p>{last_name || ''}</p>
              )}
            </div>
            <div className='section-row flex-wrap'>
              <span className='section-row-label'>Email:</span>
              { isEditing ? (
                <div className='input-wrapper w-100'>
                  <input
                    className='input'
                    type='email'
                    name='email'
                    value={email || ''}
                    placeholder='Email'
                    onChange={handleChange}
                  />
                  <ErrorMessage errorText={validationErrors.email} />
                </div>
              ) : (
                <p>{email || ''}</p>
              )}
            </div>

            <div className='section-row flex-wrap'>
              <span className='section-row-label'>Phone:</span>
              { isEditing ? (
                <div className='input-wrapper w-100'>
                  <input
                    className='input'
                    type='tel'
                    name='phone'
                    value={phone || ''}
                    placeholder='Phone'
                    onChange={handleChange}
                  />
                  <ErrorMessage errorText={validationErrors.phone} />
                </div>
              ) : (
                <p>{phone || ''}</p>
              )}
            </div>

            <div className='address-row w-100'>
              <AddressInputAutoComplete
                isDisabled={!isEditing}
                addressInfo={addressInfo}
                handleAddressChange={handleChangeAddress}
              />
              <ErrorMessage errorText={validationErrors.address} />
            </div>

            { address2IsShown && (
              <div className='section-row flex-wrap'>
                <span className='section-row-label'>Address 2:</span>
                { isEditing ? (
                  <div className='input-wrapper w-100'>
                    <input
                      className='input'
                      name='address2'
                      value={address2 || ''}
                      placeholder='Address 2'
                      onChange={handleChange}
                    />
                  </div>
                ) : (
                  <p>{address2 || ''}</p>
                )}
              </div>
            )}

            <div className='section-row align-center'>
              <span className='section-row-label'>Gender:</span>
              { isEditing ? (
                <div className='d-flex'>
                  {genderList.map(({ code, label }) => (
                    <Radio
                      name='gender'
                      key={code}
                      onChange={() => handleChange({ target: { name: 'gender', value: code } })}
                      checked={gender === code}
                      value={code}
                      label={label}
                    />
                  ))}
                </div>
              ) : (
                <p>{gender || ''}</p>
              )}
            </div>

            <div className='section-row align-center'>
              <span className='section-row-label'>Primary Language:</span>
              { isEditing ? (
                <div className='d-flex'>
                  {languagesList.map(language =>
                    <Radio
                      key={language.id}
                      onChange={handleChangePrimaryLanguage}
                      checked={language_id === language.id}
                      value={language.id}
                      name='language_id'
                      label={language.language_name}
                    />
                  )}
                </div>
              ) : (
                <p>{(userLang && userLang.language_name) || ''}</p>
              )}
            </div>

            {!!language_id && (
              <div className='section-row align-center'>
                <span className='section-row-label'>Secondary Language:</span>
                { isEditing ? (
                  <div>
                    {availableSecondaryLang.map(language =>
                      <SwitchRow
                        key={language.id}
                        checked={secondary_language_id === language.id}
                        onChange={() => handleSwitch('secondary_language_id', language.id)}
                        label={language.language_name}
                      />
                    )}
                  </div>
                ) : (
                  <p>{(secondUserLang && secondUserLang.language_name) || 'None'}</p>
                )}
              </div>
            )}

            <div className='section-row flex-wrap align-items-center'>
              <SwitchRow disabled={!isEditing} checked={has_car} onChange={() => handleSwitch('has_car')} />
              <label className='ml-2'>Has Car</label>
            </div>

            <ChangeUserPasswordModal handleChangePassword={handleChangePassword} />
          </Fragment>
        )}
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/app/workerDetails/WorkerPersonalInfo.js